import type { ComponentProps, ReactNode } from 'react';

import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import type { VariantProps } from 'tailwind-variants';

import { hoverCardStyles } from './styles/hover-card.style';

type HoverCardStyles = VariantProps<typeof hoverCardStyles>;

function HoverCardRoot(props: ComponentProps<typeof HoverCardPrimitive.Root>) {
  return <HoverCardPrimitive.Root data-slot="hover-card" {...props} />;
}

function HoverCardTrigger({
  className,
  design,
  ...rest
}: ComponentProps<typeof HoverCardPrimitive.Trigger> & HoverCardStyles) {
  const { trigger: triggerStyles } = hoverCardStyles();
  return (
    <HoverCardPrimitive.Trigger
      className={triggerStyles({ design, className })}
      data-slot="hover-card-trigger"
      {...rest}
    />
  );
}

function HoverCardPortal(
  props: ComponentProps<typeof HoverCardPrimitive.Portal>,
) {
  return <HoverCardPrimitive.Portal data-slot="hover-card-portal" {...props} />;
}

function HoverCardContent({
  className,
  design,
  sideOffset = 8,
  ...rest
}: ComponentProps<typeof HoverCardPrimitive.Content> & HoverCardStyles) {
  const { content: contentStyles } = hoverCardStyles();
  return (
    <HoverCardPrimitive.Portal>
      <HoverCardPrimitive.Content
        className={contentStyles({ design, className })}
        data-slot="hover-card-content"
        sideOffset={sideOffset}
        {...rest}
      />
    </HoverCardPrimitive.Portal>
  );
}

function HoverCardArrow({
  className,
  ...rest
}: ComponentProps<typeof HoverCardPrimitive.Arrow>) {
  return (
    <HoverCardPrimitive.Arrow
      className={className}
      data-slot="hover-card-arrow"
      {...rest}
    />
  );
}

function HoverCardWrapper({
  children,
  content,
  design,
  openDelay = 300,
  side = 'top',
}: {
  children: ReactNode;
  content: ReactNode;
  design?: HoverCardStyles['design'];
  openDelay?: number;
  side?: ComponentProps<typeof HoverCardPrimitive.Content>['side'];
}) {
  return (
    <HoverCardRoot openDelay={openDelay}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>

      <HoverCardContent design={design} side={side}>
        {content}
      </HoverCardContent>
    </HoverCardRoot>
  );
}

export const HoverCard = {
  Arrow: HoverCardArrow,
  Content: HoverCardContent,
  Portal: HoverCardPortal,
  Root: HoverCardRoot,
  Trigger: HoverCardTrigger,
  Wrapper: HoverCardWrapper,
};
